import {
  Notification,
  PermittedCompanyItem,
  RecentSearch,
  User,
} from '../models/core.models';
import {
  addRecentSearchSuccess,
  authCheck,
  fetchActivatedUserCompanies,
  fetchActivatedUserCompaniesSuccess,
  listCompanyObjectsSuccess,
  loadAlertsCountSuccess,
  loadLatestNotifications,
  loadLatestNotificationsSuccess,
  loadNotificationsCountSuccess,
  loadRecentSearchesSuccess,
  loadTriggeredAlerts,
  loadTriggeredAlertsFailure,
  loadTriggeredAlertsSuccess,
  login,
  loginFailure,
  loginSuccess,
  logout,
  remoteLogin,
  remoteLoginFail,
  remoteLoginSuccess,
  removeRecentSearchSuccess,
  setRedirectUrl,
  switchUserCompanySuccess,
  toggleExpandAlert,
} from '../actions/auth.actions';
import {
  applyFeedPostAlgorithmSuccess,
  toggleAlertModeSuccess,
} from '../actions/feed.actions';
import { createReducer, on } from '@ngrx/store';
import {
  newUserNotification,
  updateNotificationProgressSuccess,
  updateNotificationTaskTotalSuccess,
} from '../actions/user.actions';

import { Alert } from '../models/alert.models';
import { editUserSuccess } from 'src/app/admin/actions/admin-users.actions';
import { produce } from 'immer';

// Define the shape of the authentication state
export interface UserState {
  // --- auth lifecycle ---
  isAuthChecked: boolean; // Flag to check if authentication status has been checked
  isAuthLoading: boolean; // Flag to indicate if an authentication request is in progress
  isAuthenticated: boolean; // Flag to indicate if the user is authenticated
  isAuthResolved: boolean; // Flag to indicate if the authentication state status has been resolved
  isAuthError?: boolean; // Flag to indicate if there was an error during authentication
  authError?: string; // Store the error message if any during authentication

  // --- auth tokens ---
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: number | null; // unix seconds
  refreshTokenExpiresAt: number | null; // unix seconds
  // --- user ---
  user: User; // Store the authenticated user's data
  redurectUrl?: string; // Optional redirect URL after successful authentication
  // --- counts ---
  countAlerts: number; // Number of alerts
  countNotifications: number; // Number of notifications
  // --- alerts ---
  alerts: Array<Alert>;
  alertsLoading: boolean;
  alertsLoaded: boolean; // if the user alerts are already loaded
  // --- notifications ---
  notifications: Array<Notification>;
  notificationsLoading: boolean;
  notificationsLoaded: boolean;
  // --- companies / misc ---
  activatedUserCompanies: PermittedCompanyItem[];
  activatedUserCompaniesLoading: boolean;
  activatedUserCompaniesTotal?: number; // Total number of activated user companies
  listCompanyObjects: boolean;
  recentSearches: RecentSearch[];
}

// Define the initial state for authentication
export const initialState: UserState = {
  // --- auth lifecycle ---
  isAuthChecked: false,
  isAuthLoading: false,
  isAuthenticated: false,
  isAuthError: false,
  isAuthResolved: false,

  // --- auth tokens ---
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null, // unix seconds
  refreshTokenExpiresAt: null, // unix seconds

  user: null,
  countAlerts: 0,
  countNotifications: 0,
  alerts: [],
  alertsLoading: false,
  alertsLoaded: false,
  notifications: [],
  notificationsLoading: false,
  notificationsLoaded: false,
  activatedUserCompanies: [],
  activatedUserCompaniesLoading: false,
  listCompanyObjects: false,
  recentSearches: [],
};

const makeKey = (s: RecentSearch | Omit<RecentSearch, 'time_updated'>) =>
  `${s.query_text}|${JSON.stringify(s.filters ?? {})}|${s.lang ?? ''}|${s.scope_id ?? ''}`;

// Create the authentication reducer
export const userReducer = createReducer(
  initialState,
  // Handle the login action
  on(login, remoteLogin, (state) => ({
    ...state,
    isAuthLoading: true, // Set loading to true when login starts
    isAuthenticated: false,
    isAuthError: false,
    authError: null,
    user: null,
  })),
  // Handle the login success action
  on(loginSuccess, remoteLoginSuccess, (state, { response }) => ({
    ...state,
    isAuthLoading: false, // Set loading to false when login succeeds
    isAuthenticated: true, // Set authenticated to true on success
    isAuthChecked: true,
    isAuthError: false,
    authError: null,
    user: response.user,
  })),
  // Handle the login failure action
  on(loginFailure, remoteLoginFail, (state, { error }) => ({
    ...state,
    isAuthChecked: true,
    isAuthLoading: false, // Set loading to false when login fails
    isAuthenticated: false,
    isAuthError: true, // Set error flag to true on failure
    authError: error,
    user: null,
  })),
  // Handle the authentication check action
  on(authCheck, (state, {}) => ({
    ...state,
    isAuthChecked: true, // Set checked flag to true when authentication status is verified
  })),

  // ! TODO: this is not implemented yet - need proper response data for the updated Feed
  on(applyFeedPostAlgorithmSuccess, (state, { userBackgroundTask }) => {
    return {
      ...state,
    };
  }),

  on(
    updateNotificationProgressSuccess,
    produce(
      (
        draft,
        {
          notificationId,
          overallProgress,
          overallProgressMax,
          overallProgressPercent,
          isFinished,
        },
      ) => {
        const notification = draft.notifications.find(
          (n) => n.fingerprint_id == notificationId,
        );

        if (notification) {
          notification.overall_progress = overallProgress;
          notification.overall_progress_max = overallProgressMax;
          notification.overall_progress_percent = overallProgressPercent;
          notification.is_finished = isFinished;
        }
      },
    ),
  ),

  on(
    updateNotificationTaskTotalSuccess,
    produce((draft, { notificationId, total }) => {
      const notification = draft.notifications.find(
        (n) => n.fingerprint_id === notificationId,
      );

      if (notification) {
        notification.overall_progress_max = total;
      }
    }),
  ),

  on(loadNotificationsCountSuccess, (state, { count }) => {
    return {
      ...state,
      countNotifications: count,
    };
  }),

  on(loadAlertsCountSuccess, (state, { count }) => {
    return {
      ...state,
      countAlerts: count,
    };
  }),

  on(loadTriggeredAlerts, (state) => {
    return {
      ...state,
      alertsLoading: true,
    };
  }),

  on(newUserNotification, (state, { notification }) => {
    return {
      ...state,
      countNotifications: state.countNotifications + 1,
      notifications: [notification, ...state.notifications],
    };
  }),

  on(loadTriggeredAlertsSuccess, (state, { alerts }) => {
    return {
      ...state,
      alerts: alerts,
      alertsLoading: false,
      alertsLoaded: true,
    };
  }),

  on(loadTriggeredAlertsFailure, (state, { error }) => {
    return {
      ...state,
      alertsLoading: false,
      alertsLoaded: false,
    };
  }),

  on(
    toggleExpandAlert,
    produce((draft, { id }) => {
      const alert = draft.alerts.find((alert) => alert.id === id);
      if (alert) {
        alert.expanded = !alert.expanded;
      }
    }),
  ),

  on(
    toggleAlertModeSuccess,
    produce((draft, { alertId }) => {
      const index = draft.alerts.findIndex((alert) => alert.id === alertId);

      if (index !== -1) {
        draft.alerts[index].mode =
          draft.alerts[index].mode.id == 1
            ? { id: 2, title: 'Paused' }
            : { id: 1, title: 'Running' };
      }
    }),
  ),

  on(loadLatestNotifications, (state) => {
    return {
      ...state,
      notificationsLoading: true,
    };
  }),

  on(
    loadLatestNotificationsSuccess,
    produce((draft, { notifications }) => {
      const hasRead = draft.notifications.some((n) => n.is_read);

      if (hasRead) {
        draft.notifications.push(...notifications);
      } else {
        draft.notifications = notifications;
      }

      draft.notificationsLoading = false;
      draft.notificationsLoaded = true;
      draft.countNotifications = 0;
    }),
  ),

  on(fetchActivatedUserCompanies, (state) => {
    return {
      ...state,
      activatedUserCompaniesLoading: true,
    };
  }),

  on(fetchActivatedUserCompaniesSuccess, (state, { companies, total }) => {
    return {
      ...state,
      activatedUserCompanies: companies,
      activatedUserCompaniesLoading: false,
      activatedUserCompaniesTotal: total,
    };
  }),

  on(switchUserCompanySuccess, (state, { user }) => {
    return {
      ...state,
      user: user,
    };
  }),

  on(setRedirectUrl, (state, { url }) => {
    return {
      ...state,
      redurectUrl: url,
    };
  }),

  on(
    editUserSuccess,
    produce((draft, { userData }) => {
      if (draft.user.id !== userData.id) return;

      Object.assign(draft.user, userData);
    }),
  ),

  on(
    listCompanyObjectsSuccess,
    produce((draft, { listCompanyObjects }) => {
      draft.user.list_company_objects = listCompanyObjects;
    }),
  ),

  /* Load recent searches */
  on(loadRecentSearchesSuccess, (state, { searches }) => {
    const normalized = (searches ?? []).map(
      (s): RecentSearch => ({
        ...s,
        query_text: String(s.query_text ?? ''),
      }),
    );

    return {
      ...state,
      recentSearches: normalized,
    };
  }),

  /* Add recent search (optimistic merge to top, de-dupe by normalized key, cap 20) */
  on(addRecentSearchSuccess, (state, { search }) => {
    const normalized: RecentSearch = {
      ...search,
      query_text: String(search.query_text ?? ''), // force to string
    };

    const incomingKey = makeKey(normalized);
    const withoutDupes = state.recentSearches.filter(
      (s) => makeKey(s) !== incomingKey,
    );

    return {
      ...state,
      recentSearches: [normalized, ...withoutDupes].slice(0, 20),
    };
  }),

  on(removeRecentSearchSuccess, (state, { byFields }) => {
    const key = (s: RecentSearch) =>
      `${s.query_text}|${JSON.stringify(s.filters ?? {})}|${s.lang ?? ''}|${s.scope_id ?? ''}`;
    return {
      ...state,
      recentSearches: state.recentSearches.filter(
        (s) => key(s) !== key(byFields as any),
      ),
    };
  }),

  on(logout, () => initialState),
);
